/**
 * @module data/cisMockData
 * @description Datos mock para el módulo CIS Controls v8 (Center for Internet Security).
 *
 * Contiene los 18 controles con sus 153 salvaguardas completas organizadas por
 * Grupo de Implementación (IG1/IG2/IG3) y función de seguridad NIST CSF.
 * Datos pre-cargados para demostración sin conexión a Supabase.
 *
 * Fuente: CIS Controls v8 (traducción al español)
 *
 * @see CISContext para el contexto React que gestiona estos datos
 * @see types/cis.ts para los tipos CISControl, CISSalvaguarda y CISMetrics
 */

import type {
  CISControl,
  CISSalvaguarda,
  CISAuditEntry,
  CISMetrics,
  CISSecurityFunction,
  CISAssetType
} from '../types/cis';

// =====================================================
// HELPER: crear salvaguarda
// =====================================================

let _idCounter = 0;
const mkId = (prefix: string) => `${prefix}-${String(++_idCounter).padStart(4, '0')}`;

function mkSalvaguarda(
  controlId: string,
  number: string,
  title: string,
  description: string,
  ig1: boolean, ig2: boolean, ig3: boolean,
  assetType: CISAssetType,
  securityFunction: CISSecurityFunction,
  status: CISSalvaguarda['status'] = 'not_started',
  score = 0
): CISSalvaguarda {
  return {
    id: mkId('cis-s'),
    controlId,
    number,
    title,
    description,
    ig1, ig2, ig3,
    assetType,
    securityFunction,
    status,
    complianceScore: score,
    assignedReferents: [],
    tasks: [],
    evidence: [],
  };
}

// =====================================================
// CONTROL 1: Inventario y Control de Activos de la Empresa
// =====================================================
const c1Id = 'cis-ctrl-01';
const ctrl1Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c1Id, '1.1', 'Establecer y mantener un inventario detallado de activos empresariales',
    'Establecer y mantener un inventario preciso, detallado y actualizado de todos los activos empresariales con capacidad de conectarse a la infraestructura de la empresa, incluidos: dispositivos de usuario final, dispositivos de red, dispositivos no informáticos/IoT y servidores.',
    true, true, true, 'Dispositivos', 'Identificar', 'in_progress', 45),
  mkSalvaguarda(c1Id, '1.2', 'Abordar los activos no autorizados',
    'Asegurarse de que se mantiene un proceso para abordar los activos no autorizados semanalmente. El proceso de empresa puede incluir: alertar al personal de seguridad, aislar los activos no autorizados o actualizar el inventario.',
    true, true, true, 'Dispositivos', 'Responder', 'not_started', 0),
  mkSalvaguarda(c1Id, '1.3', 'Utilizar una herramienta de descubrimiento activo',
    'Utilizar una herramienta de descubrimiento activo para identificar los activos conectados a la infraestructura de la empresa. Configurar la herramienta para ejecutarse diariamente o con mayor frecuencia.',
    false, true, true, 'Dispositivos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c1Id, '1.4', 'Usar el registro de DHCP para actualizar el inventario de activos empresariales',
    'Usar los registros del protocolo de configuración dinámica de host (DHCP) para actualizar el inventario de activos empresariales.',
    false, true, true, 'Dispositivos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c1Id, '1.5', 'Usar una herramienta de descubrimiento pasivo',
    'Usar una herramienta de descubrimiento pasivo para identificar activos conectados a la infraestructura de la empresa.',
    false, false, true, 'Dispositivos', 'Identificar', 'not_started', 0),
];

// =====================================================
// CONTROL 2: Inventario y Control de Activos de Software
// =====================================================
const c2Id = 'cis-ctrl-02';
const ctrl2Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c2Id, '2.1', 'Establecer y mantener un inventario de software',
    'Establecer y mantener un inventario de todo el software de la empresa. Incluir el software del sistema operativo.',
    true, true, true, 'Aplicaciones', 'Identificar', 'in_progress', 60),
  mkSalvaguarda(c2Id, '2.2', 'Asegurar que el software esté autorizado',
    'Asegurar que el software autorizado siga siendo realmente necesario para los activos empresariales. Revisar semestralmente o con mayor frecuencia.',
    true, true, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c2Id, '2.3', 'Abordar el software no autorizado',
    'Asegurar que el software no autorizado se elimine de los activos empresariales o que la excepción esté documentada.',
    true, true, true, 'Aplicaciones', 'Responder', 'not_started', 0),
  mkSalvaguarda(c2Id, '2.4', 'Utilizar herramientas de inventario de software automatizadas',
    'Utilizar herramientas de inventario de software automatizadas para hacer cumplir la lista de software permitido.',
    false, true, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c2Id, '2.5', 'Incluir código ejecutable en el inventario de software',
    'Incluir todos los tipos de código ejecutable en el inventario de software, incluidos los tipos de archivos ejecutables no estándar.',
    false, true, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c2Id, '2.6', 'Incluir bibliotecas de software en el inventario de software',
    'Incluir las bibliotecas de software en el inventario de activos de software de la empresa.',
    false, true, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c2Id, '2.7', 'Usar listas de materiales de software',
    'Usar listas de materiales de software (SBOM) para gestionar los componentes de terceros.',
    false, false, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
];

// =====================================================
// CONTROL 3: Protección de Datos
// =====================================================
const c3Id = 'cis-ctrl-03';
const ctrl3Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c3Id, '3.1', 'Establecer y mantener un proceso de gestión de datos',
    'Establecer y mantener un proceso de gestión de datos. En el proceso se deben abordar la sensibilidad de los datos, su propietario, el manejo de datos y la eliminación de datos.',
    true, true, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.2', 'Establecer y mantener un inventario de datos',
    'Establecer y mantener un inventario de datos. Al mínimo, cataloga los datos confidenciales.',
    false, true, true, 'Datos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.3', 'Configurar las listas de control de acceso a datos',
    'Configurar las listas de control de acceso a los datos basándose en la necesidad del usuario de conocer los datos sensibles.',
    true, true, true, 'Datos', 'Proteger', 'implemented', 100),
  mkSalvaguarda(c3Id, '3.4', 'Aplicar la retención de datos',
    'Retener los datos de acuerdo con la política de retención de datos de la empresa.',
    false, true, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.5', 'Eliminar los datos de forma segura',
    'Eliminar de forma segura los datos según se determine por la sensibilidad de los datos.',
    true, true, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.6', 'Cifrar los datos en los dispositivos de usuario final',
    'Cifrar los datos en los dispositivos de usuario final que contengan datos confidenciales.',
    false, true, true, 'Dispositivos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.7', 'Establecer y mantener un proceso de descubrimiento de datos',
    'Establecer y mantener un proceso general de descubrimiento de datos.',
    false, true, true, 'Datos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.8', 'Documentar los datos confidenciales',
    'Documentar los datos confidenciales.',
    false, true, true, 'Datos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.9', 'Cifrar los datos en medios extraíbles',
    'Cifrar los datos en medios extraíbles.',
    false, true, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.10', 'Cifrar los datos confidenciales en tránsito',
    'Cifrar los datos confidenciales en tránsito.',
    false, true, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.11', 'Cifrar los datos confidenciales en reposo',
    'Cifrar los datos confidenciales en reposo en servidores, aplicaciones y bases de datos.',
    false, false, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.12', 'Segmentar el procesamiento y el almacenamiento de datos',
    'Segmentar el procesamiento y el almacenamiento de datos en función de la sensibilidad de los datos.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.13', 'Implementar protección de datos',
    'Implementar una solución automatizada de prevención de pérdida de datos (DLP).',
    false, false, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c3Id, '3.14', 'Registrar el acceso a los datos confidenciales',
    'Registrar el acceso a los datos confidenciales, incluida la modificación y eliminación.',
    false, false, true, 'Datos', 'Detectar', 'not_started', 0),
];

// =====================================================
// CONTROL 4: Configuración Segura de Activos Empresariales y Software
// =====================================================
const c4Id = 'cis-ctrl-04';
const ctrl4Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c4Id, '4.1', 'Establecer y mantener un proceso de configuración segura',
    'Establecer y mantener un proceso de configuración segura para los activos empresariales y el software de la empresa.',
    true, true, true, 'Dispositivos', 'Proteger', 'in_progress', 30),
  mkSalvaguarda(c4Id, '4.2', 'Establecer y mantener una configuración segura para los sistemas operativos de red',
    'Establecer y mantener una configuración segura para los sistemas operativos de red, incluidos al menos routers y switches.',
    true, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.3', 'Configurar la autenticación automática del sistema operativo',
    'Configurar el bloqueo automático en los activos empresariales tras un número determinado de intentos de autenticación incorrectos.',
    true, true, true, 'Dispositivos', 'Proteger', 'implemented', 100),
  mkSalvaguarda(c4Id, '4.4', 'Implementar y gestionar un firewall basado en host en los activos empresariales',
    'Implementar y gestionar un firewall basado en host o una herramienta de filtrado de puertos en los activos empresariales.',
    true, true, true, 'Dispositivos', 'Proteger', 'in_progress', 70),
  mkSalvaguarda(c4Id, '4.5', 'Implementar y gestionar un firewall basado en host en los servidores',
    'Implementar y gestionar un firewall basado en host o una herramienta de filtrado de puertos en los servidores.',
    false, true, true, 'Dispositivos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.6', 'Gestionar de forma segura los activos empresariales y el software',
    'Gestionar de forma segura los activos empresariales y el software. Solo se debe permitir el acceso remoto de administración a través de canales seguros.',
    false, true, true, 'Dispositivos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.7', 'Gestionar los proveedores de acceso remoto predeterminados',
    'Gestionar los accesos remotos predeterminados en los activos empresariales.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.8', 'Desinstalar o deshabilitar los servicios innecesarios',
    'Desinstalar o deshabilitar los servicios, funciones y componentes innecesarios en los activos empresariales.',
    false, true, true, 'Dispositivos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.9', 'Configurar los puertos, protocolos y servicios DNS de confianza en los activos',
    'Configurar los puertos, protocolos y servicios de confianza de sistemas de nombre de dominio (DNS) en los activos empresariales.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.10', 'Implementar la gestión automática de la configuración',
    'Implementar y gestionar una solución de evaluación de la configuración.',
    false, false, true, 'Dispositivos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.11', 'Recuperar datos de configuración de forma segura',
    'Salvaguardar los datos de configuración con tecnología de recuperación de configuración.',
    false, false, true, 'Dispositivos', 'Recuperar', 'not_started', 0),
  mkSalvaguarda(c4Id, '4.12', 'Separar los activos empresariales de los activos de infraestructura',
    'Separar los activos empresariales de los activos de infraestructura utilizando una separación de red o lógica.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 5: Gestión de Cuentas
// =====================================================
const c5Id = 'cis-ctrl-05';
const ctrl5Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c5Id, '5.1', 'Establecer y mantener un inventario de cuentas',
    'Establecer y mantener un inventario de todas las cuentas gestionadas en la empresa.',
    true, true, true, 'Usuarios', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c5Id, '5.2', 'Usar contraseñas únicas',
    'Usar contraseñas únicas para todos los activos empresariales. Las mejores prácticas sugieren seguir las directrices de contraseñas del NIST 800-63B.',
    true, true, true, 'Usuarios', 'Proteger', 'implemented', 100),
  mkSalvaguarda(c5Id, '5.3', 'Deshabilitar las cuentas inactivas',
    'Deshabilitar las cuentas de usuario inactivas después de un período de inactividad de 45 días.',
    true, true, true, 'Usuarios', 'Proteger', 'in_progress', 50),
  mkSalvaguarda(c5Id, '5.4', 'Restringir los privilegios de administrador a las cuentas de administrador dedicadas',
    'Restringir los privilegios de administrador a cuentas de administrador dedicadas en los activos empresariales.',
    true, true, true, 'Usuarios', 'Proteger', 'in_progress', 80),
  mkSalvaguarda(c5Id, '5.5', 'Establecer y mantener un inventario de cuentas de servicio',
    'Establecer y mantener un inventario de las cuentas de servicio. El inventario debe incluir departamento propietario, fecha de revisión y propósito.',
    false, true, true, 'Usuarios', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c5Id, '5.6', 'Centralizar la gestión de cuentas',
    'Centralizar la gestión de cuentas mediante un directorio o servicio de identidad.',
    false, true, true, 'Usuarios', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 6: Gestión del Control de Acceso
// =====================================================
const c6Id = 'cis-ctrl-06';
const ctrl6Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c6Id, '6.1', 'Establecer un proceso de control de acceso',
    'Establecer un proceso de control de acceso que incluya aprovisionamiento y desaprovisionamiento de acceso basado en roles o atributos para la empresa.',
    true, true, true, 'Usuarios', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c6Id, '6.2', 'Establecer un inventario de sistemas de autenticación y autorización',
    'Establecer y mantener un inventario de los sistemas de autenticación y autorización de la empresa.',
    false, true, true, 'Usuarios', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c6Id, '6.3', 'Requerir MFA para aplicaciones expuestas externamente',
    'Requerir toda la autenticación de aplicaciones de red corporativas expuestas externamente mediante autenticación multifactor (MFA).',
    true, true, true, 'Usuarios', 'Proteger', 'in_progress', 60),
  mkSalvaguarda(c6Id, '6.4', 'Requerir MFA para el acceso remoto a la red',
    'Requerir MFA para el acceso remoto a la red.',
    true, true, true, 'Usuarios', 'Proteger', 'implemented', 100),
  mkSalvaguarda(c6Id, '6.5', 'Requerir MFA para el acceso administrativo',
    'Requerir MFA para todos los accesos administrativos, incluido el acceso administrativo de nivel de dominio.',
    false, true, true, 'Usuarios', 'Proteger', 'in_progress', 50),
  mkSalvaguarda(c6Id, '6.6', 'Establecer y mantener un inventario de sistemas de autenticación',
    'Establecer y mantener un inventario de los sistemas de autenticación de la empresa, incluidos los de terceros.',
    false, true, true, 'Usuarios', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c6Id, '6.7', 'Centralizar el control de acceso',
    'Centralizar el control de acceso para todos los activos empresariales mediante un directorio o servicio de identidad.',
    false, true, true, 'Usuarios', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c6Id, '6.8', 'Definir y mantener el control de acceso basado en roles',
    'Definir y mantener el control de acceso basado en roles (RBAC) para todos los activos empresariales.',
    false, false, true, 'Usuarios', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 7: Gestión Continua de Vulnerabilidades
// =====================================================
const c7Id = 'cis-ctrl-07';
const ctrl7Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c7Id, '7.1', 'Establecer y mantener un proceso de gestión de vulnerabilidades',
    'Establecer y mantener un proceso documentado de gestión de vulnerabilidades para los activos empresariales.',
    true, true, true, 'Dispositivos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c7Id, '7.2', 'Establecer y mantener un sistema de remediación',
    'Establecer y mantener un sistema de remediación para repasar y priorizar las vulnerabilidades basado en la gravedad.',
    true, true, true, 'Dispositivos', 'Responder', 'not_started', 0),
  mkSalvaguarda(c7Id, '7.3', 'Realizar escaneos de vulnerabilidades automatizados de los activos empresariales',
    'Realizar escaneos de vulnerabilidades automatizados de los activos empresariales con y sin autenticación de forma mensual o más frecuentemente.',
    false, true, true, 'Dispositivos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c7Id, '7.4', 'Realizar escaneos de vulnerabilidades automatizados de las aplicaciones internas',
    'Realizar escaneos de vulnerabilidades automatizados de las aplicaciones internas, de forma semanal o más frecuentemente.',
    false, true, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c7Id, '7.5', 'Realizar escaneos de vulnerabilidades automatizados de las aplicaciones orientadas externamente',
    'Realizar escaneos de vulnerabilidades automatizados de las aplicaciones orientadas externamente, de forma mensual o más frecuentemente.',
    false, true, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c7Id, '7.6', 'Remediar las vulnerabilidades detectadas',
    'Remediar las vulnerabilidades detectadas de acuerdo con las instrucciones del sistema de remediación de la empresa.',
    false, true, true, 'Dispositivos', 'Responder', 'not_started', 0),
  mkSalvaguarda(c7Id, '7.7', 'Remediar las vulnerabilidades detectadas en los sistemas de alto riesgo',
    'Remediar las vulnerabilidades detectadas en los sistemas de alto riesgo de acuerdo con las instrucciones del sistema de remediación.',
    false, false, true, 'Dispositivos', 'Responder', 'not_started', 0),
];

// =====================================================
// CONTROL 8: Gestión de Registros de Auditoría
// =====================================================
const c8Id = 'cis-ctrl-08';
const ctrl8Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c8Id, '8.1', 'Establecer y mantener un proceso de gestión de registros de auditoría',
    'Establecer y mantener un proceso documentado de gestión de registros de auditoría.',
    true, true, true, 'Datos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.2', 'Recopilar registros de auditoría',
    'Recopilar registros de auditoría. Asegurar que el registro de auditoría incluya la fecha y la hora del evento, la fuente del evento, el tipo de evento y el resultado del evento.',
    true, true, true, 'Dispositivos', 'Detectar', 'in_progress', 40),
  mkSalvaguarda(c8Id, '8.3', 'Asegurar que los registros de auditoría sean adecuados',
    'Asegurar que la infraestructura de registro incluya logs de control de acceso físico, servidores de control de red, proxies y DNS.',
    false, true, true, 'Red', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.4', 'Estandarizar la recopilación de registros de tiempo',
    'Estandarizar la sincronización de tiempo. Configurar al menos dos fuentes de tiempo sincronizadas a través de los activos empresariales.',
    false, true, true, 'Dispositivos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.5', 'Recopilar registros de auditoría detallados',
    'Configurar la auditoría de los activos empresariales para recopilar registros detallados de eventos, incluidas las fuentes de autenticación de usuarios.',
    false, true, true, 'Dispositivos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.6', 'Recopilar los registros de auditoría de DNS',
    'Recopilar los registros de auditoría de DNS en todos los activos empresariales.',
    false, true, true, 'Red', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.7', 'Recopilar los registros de auditoría de flujos de red URL',
    'Recopilar los registros de auditoría de flujos de red URL de los activos empresariales.',
    false, true, true, 'Red', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.8', 'Gestionar centralizadamente los registros de auditoría',
    'Gestionar centralizadamente los registros de auditoría.',
    false, true, true, 'Datos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.9', 'Retener los registros de auditoría',
    'Retener los registros de auditoría durante al menos 90 días.',
    false, false, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.10', 'Gestionar los registros de auditoría de proveedor de servicios en la nube',
    'Gestionar los registros de auditoría del proveedor de servicios en la nube.',
    false, false, true, 'Datos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.11', 'Llevar a cabo revisiones de los registros de auditoría',
    'Llevar a cabo revisiones periódicas de los registros de auditoría. La frecuencia de revisión debe depender de la sensibilidad del activo.',
    false, false, true, 'Datos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c8Id, '8.12', 'Recopilar registros de auditoría del proveedor de servicios',
    'Recopilar los registros de auditoría del proveedor de servicios para determinar la actividad de los proveedores de servicios externos.',
    false, false, true, 'Datos', 'Detectar', 'not_started', 0),
];

// =====================================================
// CONTROL 9: Protecciones de Correo Electrónico y Navegador Web
// =====================================================
const c9Id = 'cis-ctrl-09';
const ctrl9Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c9Id, '9.1', 'Asegurar el uso de navegadores web y clientes de correo electrónico compatibles',
    'Asegurar que solo se usen navegadores web y clientes de correo electrónico totalmente compatibles (con actualizaciones de seguridad).',
    true, true, true, 'Aplicaciones', 'Proteger', 'implemented', 100),
  mkSalvaguarda(c9Id, '9.2', 'Usar servicios de DNS de filtrado en todos los activos empresariales',
    'Usar servicios de DNS de filtrado en todos los activos empresariales para bloquear el acceso a dominios maliciosos conocidos.',
    true, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c9Id, '9.3', 'Mantener y aplicar filtros de URL basados en redes',
    'Aplicar y actualizar filtros de URL basados en redes para limitar los tipos de sitios web a los que puede acceder el personal.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c9Id, '9.4', 'Restringir las extensiones de navegador web innecesarias o no autorizadas',
    'Restringir, ya sea a través de listas de permitidos o bloqueados, la capacidad de instalar y ejecutar extensiones de navegador.',
    false, true, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c9Id, '9.5', 'Implementar DMARC',
    'Para reducir la posibilidad de suplantación de dominio de correo electrónico, implementar DMARC en todos los dominios de correo electrónico.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c9Id, '9.6', 'Bloquear los tipos de archivos de correo electrónico innecesarios',
    'Bloquear los tipos de archivos de correo electrónico innecesarios que no se utilizan habitualmente en la empresa.',
    false, true, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c9Id, '9.7', 'Implementar protección de la capa de DNS',
    'Usar tecnologías DNS anti-malware para bloquear el acceso a categorías de dominios no autorizados.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 10: Defensas contra Malware
// =====================================================
const c10Id = 'cis-ctrl-10';
const ctrl10Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c10Id, '10.1', 'Implementar herramientas anti-malware',
    'Implementar y mantener herramientas anti-malware en todos los activos empresariales.',
    true, true, true, 'Dispositivos', 'Proteger', 'implemented', 100),
  mkSalvaguarda(c10Id, '10.2', 'Configurar la actualización automática de firmas anti-malware',
    'Configurar las actualizaciones automáticas para las firmas anti-malware en todos los activos empresariales.',
    true, true, true, 'Dispositivos', 'Proteger', 'implemented', 100),
  mkSalvaguarda(c10Id, '10.3', 'Deshabilitar la ejecución automática y la reproducción automática para medios extraíbles',
    'Deshabilitar la ejecución automática y la reproducción automática para medios extraíbles.',
    true, true, true, 'Dispositivos', 'Proteger', 'in_progress', 70),
  mkSalvaguarda(c10Id, '10.4', 'Configurar el escaneo automático de medios extraíbles',
    'Configurar el escaneo automático de medios extraíbles al conectarse.',
    false, true, true, 'Dispositivos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c10Id, '10.5', 'Habilitar capacidades anti-explotación',
    'Habilitar las capacidades anti-explotación en los activos empresariales.',
    false, true, true, 'Dispositivos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c10Id, '10.6', 'Gestionar centralizadamente el software anti-malware',
    'Gestionar centralizadamente el software anti-malware.',
    false, true, true, 'Dispositivos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c10Id, '10.7', 'Usar soluciones EDR basadas en el comportamiento',
    'Usar soluciones de detección y respuesta de endpoints (EDR) basadas en el comportamiento.',
    false, false, true, 'Dispositivos', 'Detectar', 'not_started', 0),
];

// =====================================================
// CONTROL 11: Recuperación de Datos
// =====================================================
const c11Id = 'cis-ctrl-11';
const ctrl11Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c11Id, '11.1', 'Establecer y mantener un proceso de recuperación de datos',
    'Establecer y mantener un proceso de recuperación de datos que incluya la definición de alcance de datos, el período de recuperación y la ruta de escalada de recuperación.',
    true, true, true, 'Datos', 'Recuperar', 'not_started', 0),
  mkSalvaguarda(c11Id, '11.2', 'Realizar copias de seguridad automatizadas',
    'Realizar copias de seguridad automatizadas de los activos empresariales. El alcance de la copia de seguridad debe incluir, como mínimo, datos confidenciales de la empresa.',
    true, true, true, 'Datos', 'Recuperar', 'in_progress', 50),
  mkSalvaguarda(c11Id, '11.3', 'Proteger los datos de recuperación',
    'Proteger los datos de recuperación utilizando los principios del privilegio mínimo.',
    true, true, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c11Id, '11.4', 'Establecer y mantener un proceso de recuperación aislado',
    'Establecer y mantener un proceso de recuperación aislado de la instancia de producción. Esto puede realizarse mediante la gestión de versiones en el almacenamiento de copias de seguridad.',
    false, true, true, 'Datos', 'Recuperar', 'not_started', 0),
  mkSalvaguarda(c11Id, '11.5', 'Probar los datos de recuperación',
    'Probar los datos de recuperación trimestralmente, o con mayor frecuencia, ejecutando una recuperación de datos de prueba de la copia de seguridad de producción.',
    false, true, true, 'Datos', 'Recuperar', 'not_started', 0),
];

// =====================================================
// CONTROL 12: Gestión de Infraestructura de Red
// =====================================================
const c12Id = 'cis-ctrl-12';
const ctrl12Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c12Id, '12.1', 'Asegurar la infraestructura de red',
    'Asegurar la infraestructura de red, como los routers y los switches, al aplicar las actualizaciones de seguridad del proveedor de hardware y al deshabilitar los protocolos innecesarios.',
    true, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c12Id, '12.2', 'Establecer y mantener una arquitectura de red segura',
    'Establecer y mantener una arquitectura de red segura. La arquitectura debe abordar la segmentación, el privilegio mínimo y la disponibilidad.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c12Id, '12.3', 'Gestionar de forma segura la infraestructura de red',
    'Gestionar la infraestructura de red de forma segura, utilizando la autenticación de dos factores para las conexiones de administración.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c12Id, '12.4', 'Establecer y mantener la arquitectura de red',
    'Establecer y mantener la arquitectura de la red. Revisar y actualizar la documentación anualmente o cuando se produzcan cambios importantes.',
    false, false, true, 'Red', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c12Id, '12.5', 'Centralizar la autenticación de red, la autorización y la auditoría',
    'Centralizar la autenticación de red, la autorización y la auditoría (AAA).',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c12Id, '12.6', 'Usar clientes VPN seguros',
    'Usar clientes VPN seguros en los activos empresariales para los accesos remotos.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c12Id, '12.7', 'Asegurar de forma remota los dispositivos de infraestructura de red',
    'Asegurar de forma remota los dispositivos de infraestructura de red mediante el acceso a la red de confianza cero.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c12Id, '12.8', 'Establecer una red dedicada para la gestión de la infraestructura de red',
    'Establecer una red dedicada para la gestión de la infraestructura de red.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 13: Monitoreo y Defensa de la Red
// =====================================================
const c13Id = 'cis-ctrl-13';
const ctrl13Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c13Id, '13.1', 'Centralizar las alertas de seguridad',
    'Centralizar las alertas de seguridad de los activos empresariales con una solución de gestión de eventos e información de seguridad (SIEM) o equivalente.',
    true, true, true, 'Red', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.2', 'Implementar una solución de filtrado de contenido DNS',
    'Implementar una solución de filtrado de contenido DNS.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.3', 'Implementar herramientas de SIEM',
    'Implementar una herramienta de gestión de eventos e información de seguridad (SIEM) para centralizar y correlacionar los registros de seguridad.',
    false, true, true, 'Red', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.4', 'Realizar escaneos de aplicaciones web',
    'Realizar escaneos de aplicaciones web para detectar vulnerabilidades en las aplicaciones orientadas externamente.',
    false, true, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.5', 'Gestionar el control de acceso para los activos de infraestructura de red',
    'Gestionar el control de acceso para los activos de infraestructura de red de forma remota.',
    false, true, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.6', 'Recopilar registros de red',
    'Recopilar los registros de red, incluidos los flujos de red, a través de los dispositivos de infraestructura de red.',
    false, true, true, 'Red', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.7', 'Implementar una herramienta de filtrado de paquetes profundos',
    'Implementar una herramienta de filtrado de paquetes profundos (DPI) para identificar amenazas avanzadas.',
    false, false, true, 'Red', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.8', 'Implementar un HIDS basado en el host',
    'Implementar un sistema de detección de intrusiones basado en el host (HIDS) o en el comportamiento.',
    false, false, true, 'Dispositivos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.9', 'Implementar una arquitectura de seguridad Zero Trust',
    'Implementar una arquitectura de seguridad de confianza cero.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.10', 'Realizar pruebas de penetración centradas en la red',
    'Realizar pruebas de penetración centradas en la red, ya sea con herramientas automatizadas o manuales.',
    false, false, true, 'Red', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c13Id, '13.11', 'Afinar los controles de seguridad',
    'Afinar y optimizar los controles de seguridad basados en los resultados de las pruebas de penetración.',
    false, false, true, 'Red', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 14: Concientización y Entrenamiento en Seguridad
// =====================================================
const c14Id = 'cis-ctrl-14';
const ctrl14Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c14Id, '14.1', 'Establecer y mantener un programa de concientización sobre seguridad',
    'Establecer y mantener un programa de concientización sobre seguridad. El programa debe abordar los temas relevantes para la empresa.',
    true, true, true, 'Usuarios', 'Proteger', 'in_progress', 30),
  mkSalvaguarda(c14Id, '14.2', 'Entrenar en reconocimiento y reporte de ataques de ingeniería social',
    'Entrenar a los miembros de la fuerza de trabajo para reconocer los ataques de ingeniería social, como phishing, pretexting y vishing.',
    true, true, true, 'Usuarios', 'Proteger', 'in_progress', 50),
  mkSalvaguarda(c14Id, '14.3', 'Entrenar en mejores prácticas de autenticación',
    'Entrenar a los miembros de la fuerza de trabajo en las mejores prácticas de autenticación, como la MFA y la seguridad de contraseñas.',
    true, true, true, 'Usuarios', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c14Id, '14.4', 'Entrenar en mejores prácticas de manejo de datos',
    'Entrenar a los miembros de la fuerza de trabajo en las mejores prácticas de manejo de datos basadas en la política de manejo de datos de la empresa.',
    true, true, true, 'Usuarios', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c14Id, '14.5', 'Entrenar sobre causas y efectos de exposición de datos',
    'Entrenar a los miembros de la fuerza de trabajo en las causas y efectos de la exposición de datos.',
    true, true, true, 'Usuarios', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c14Id, '14.6', 'Entrenar en reconocimiento y reporte de incidentes de seguridad',
    'Entrenar a los miembros de la fuerza de trabajo para reconocer un posible incidente de seguridad y notificarlo al personal de seguridad.',
    true, true, true, 'Usuarios', 'Responder', 'not_started', 0),
  mkSalvaguarda(c14Id, '14.7', 'Entrenar en identificación y reporte de exposición de información de empresa',
    'Entrenar a los miembros de la fuerza de trabajo en cómo identificar y reportar si su información de empresa ha sido expuesta.',
    false, true, true, 'Usuarios', 'Responder', 'not_started', 0),
  mkSalvaguarda(c14Id, '14.8', 'Entrenar sobre peligros de redes inalámbricas no seguras',
    'Entrenar a los miembros de la fuerza de trabajo sobre los peligros de conectarse a redes inalámbricas no seguras.',
    false, true, true, 'Usuarios', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c14Id, '14.9', 'Entrenar a los desarrolladores en prácticas de codificación segura',
    'Entrenar a los desarrolladores de aplicaciones en prácticas de codificación segura.',
    false, false, true, 'Usuarios', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 15: Gestión de Proveedores de Servicios
// =====================================================
const c15Id = 'cis-ctrl-15';
const ctrl15Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c15Id, '15.1', 'Establecer y mantener un inventario de proveedores de servicios',
    'Establecer y mantener un inventario de proveedores de servicios. El inventario debe enumerar todos los proveedores de servicios conocidos.',
    true, true, true, 'Datos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c15Id, '15.2', 'Establecer y mantener una política de gestión de proveedores de servicios',
    'Establecer y mantener una política de gestión de proveedores de servicios.',
    false, true, true, 'Datos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c15Id, '15.3', 'Clasificar a los proveedores de servicios',
    'Clasificar a los proveedores de servicios.',
    false, true, true, 'Datos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c15Id, '15.4', 'Asegurar que los SLA incluyan los requisitos de seguridad',
    'Asegurar que los acuerdos de nivel de servicio de los proveedores de servicios incluyan requisitos de seguridad y privacidad.',
    false, true, true, 'Datos', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c15Id, '15.5', 'Evaluar a los proveedores de servicios',
    'Evaluar a los proveedores de servicios de acuerdo con la política de gestión de proveedores de servicios de la empresa.',
    false, true, true, 'Datos', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c15Id, '15.6', 'Monitorear a los proveedores de servicios',
    'Monitorear a los proveedores de servicios de forma consistente.',
    false, false, true, 'Datos', 'Detectar', 'not_started', 0),
  mkSalvaguarda(c15Id, '15.7', 'Aplicar requisitos mínimos de seguridad y privacidad para los proveedores',
    'Aplicar los requisitos mínimos de seguridad y privacidad para los proveedores de servicios de terceros.',
    false, false, true, 'Datos', 'Proteger', 'not_started', 0),
];

// =====================================================
// CONTROL 16: Seguridad del Software de la Aplicación
// =====================================================
const c16Id = 'cis-ctrl-16';
const ctrl16Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c16Id, '16.1', 'Establecer y mantener un proceso seguro de desarrollo de aplicaciones',
    'Establecer y mantener un proceso de desarrollo de aplicaciones seguro.',
    false, true, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.2', 'Establecer y mantener un proceso de aceptación de aplicaciones de software',
    'Establecer y mantener un proceso de aceptación para las aplicaciones de software de terceros.',
    false, true, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.3', 'Realizar análisis de composición de software',
    'Realizar análisis de composición de software para identificar bibliotecas de terceros riesgosas o vencidas.',
    false, true, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.4', 'Establecer y gestionar un proceso de advertencia de vulnerabilidades del software',
    'Establecer y gestionar un proceso para recibir y actuar sobre las divulgaciones de vulnerabilidades de software.',
    false, true, true, 'Aplicaciones', 'Responder', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.5', 'Usar un repositorio de código fuente',
    'Usar un repositorio de código fuente con control de versiones para alojar y gestionar el código de la aplicación.',
    false, true, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.6', 'Implementar un sistema de automatización de pruebas de software',
    'Implementar un sistema de automatización de pruebas de software en el proceso de desarrollo de software.',
    false, true, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.7', 'Establecer y usar un proceso de gestión de vulnerabilidades de aplicaciones web',
    'Establecer y usar un proceso de gestión de vulnerabilidades de aplicaciones web para detectar, rastrear y remediar las vulnerabilidades.',
    false, true, true, 'Aplicaciones', 'Responder', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.8', 'Separar el sistema de producción de los sistemas no de producción',
    'Separar los entornos de desarrollo, prueba y producción.',
    false, false, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.9', 'Capacitar a los desarrolladores de aplicaciones en prácticas de codificación segura',
    'Garantizar que los desarrolladores de aplicaciones recibirán capacitación adecuada en prácticas de codificación segura.',
    false, false, true, 'Usuarios', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.10', 'Aplicar principios de diseño seguro en arquitecturas de aplicaciones',
    'Aplicar principios de diseño de seguridad en la arquitectura de las aplicaciones.',
    false, false, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.11', 'Aprovechar marcos de seguridad probados para desarrolladores de aplicaciones',
    'Aprovechar los marcos de seguridad probados de los desarrolladores de aplicaciones.',
    false, false, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.12', 'Implementar el cifrado de código',
    'Implementar el cifrado de código y la ofuscación para el código de la aplicación.',
    false, false, true, 'Aplicaciones', 'Proteger', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.13', 'Llevar a cabo revisiones periódicas de las aplicaciones',
    'Llevar a cabo revisiones periódicas de las aplicaciones con el objetivo de identificar posibles vulnerabilidades.',
    false, false, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c16Id, '16.14', 'Llevar a cabo auditorías de seguridad de aplicaciones estáticas',
    'Llevar a cabo pruebas de seguridad de aplicaciones estáticas (SAST) para todas las aplicaciones de desarrollo interno.',
    false, false, true, 'Aplicaciones', 'Identificar', 'not_started', 0),
];

// =====================================================
// CONTROL 17: Gestión de Respuesta a Incidentes
// =====================================================
const c17Id = 'cis-ctrl-17';
const ctrl17Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c17Id, '17.1', 'Designar personal para gestionar el manejo de incidentes',
    'Designar a una persona, así como al menos a un sustituto, que gestionará el proceso de manejo de incidentes de la empresa.',
    true, true, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.2', 'Establecer y mantener políticas y procedimientos de contacto',
    'Establecer y mantener políticas y procedimientos de contacto para reportar incidentes de seguridad.',
    true, true, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.3', 'Establecer y mantener un proceso de respuesta a incidentes empresariales',
    'Establecer y mantener un proceso documentado de respuesta a incidentes empresariales.',
    false, true, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.4', 'Asignar roles y responsabilidades clave de manejo de incidentes',
    'Asignar funciones de manejo clave y responsabilidades para la respuesta a incidentes.',
    false, true, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.5', 'Asignar roles clave para la comunicación del incidente',
    'Asignar funciones de manejo clave y responsabilidades para la comunicación del incidente al personal de seguridad y a las partes interesadas.',
    false, true, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.6', 'Llevar a cabo revisiones posteriores a los incidentes',
    'Llevar a cabo revisiones posteriores a los incidentes.',
    false, true, true, 'N/A', 'Recuperar', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.7', 'Llevar a cabo simulaciones periódicas de respuesta a incidentes',
    'Planificar y llevar a cabo simulaciones periódicas de respuesta a incidentes.',
    false, false, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.8', 'Llevar a cabo ejercicios de mesa de respuesta a incidentes',
    'Llevar a cabo ejercicios de mesa de respuesta a incidentes.',
    false, false, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c17Id, '17.9', 'Llevar a cabo una prueba de respuesta a incidentes anual',
    'Llevar a cabo una prueba de respuesta a incidentes de tabla anual.',
    false, false, true, 'N/A', 'Responder', 'not_started', 0),
];

// =====================================================
// CONTROL 18: Pruebas de Penetración
// =====================================================
const c18Id = 'cis-ctrl-18';
const ctrl18Salvaguardas: CISSalvaguarda[] = [
  mkSalvaguarda(c18Id, '18.1', 'Establecer y mantener un programa de pruebas de penetración',
    'Establecer y mantener un programa de pruebas de penetración según las directrices específicas del sector.',
    false, true, true, 'N/A', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c18Id, '18.2', 'Realizar pruebas de penetración externas',
    'Realizar pruebas de penetración externas según las pautas del sector y las necesidades de la organización. Los resultados de las pruebas de penetración se compararán con el año anterior para identificar las mejoras.',
    false, true, true, 'N/A', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c18Id, '18.3', 'Remediar los hallazgos de las pruebas de penetración',
    'Remediar los hallazgos identificados de las pruebas de penetración.',
    false, true, true, 'N/A', 'Responder', 'not_started', 0),
  mkSalvaguarda(c18Id, '18.4', 'Validar los controles de seguridad',
    'Validar los controles de seguridad de la empresa realizando pruebas de penetración comprensivas.',
    false, false, true, 'N/A', 'Identificar', 'not_started', 0),
  mkSalvaguarda(c18Id, '18.5', 'Realizar pruebas de penetración internas periódicas',
    'Realizar pruebas de penetración internas periódicas basadas en el programa de pruebas de penetración de la empresa.',
    false, false, true, 'N/A', 'Identificar', 'not_started', 0),
];

// =====================================================
// CONTROLES COMPLETOS
// =====================================================

function calcControl(id: string, number: number, title: string, description: string, secFn: CISSecurityFunction, salvaguardas: CISSalvaguarda[]): CISControl {
  const implemented = salvaguardas.filter(s => s.status === 'implemented').length;
  const inProgress = salvaguardas.filter(s => s.status === 'in_progress').length;
  const applicable = salvaguardas.filter(s => s.status !== 'not_applicable').length;
  const pct = applicable > 0
    ? Math.round(salvaguardas.reduce((acc, s) => acc + s.complianceScore, 0) / salvaguardas.length)
    : 0;
  return { id, number, title, description, securityFunction: secFn, salvaguardas, totalSalvaguardas: salvaguardas.length, implemented, inProgress, compliancePercentage: pct };
}

export const mockCISControls: CISControl[] = [
  calcControl(c1Id, 1, 'Inventario y Control de Activos de la Empresa',
    'Gestionar activamente (inventariar, rastrear y corregir) todos los activos empresariales (dispositivos de usuario final, dispositivos de red, dispositivos no informáticos/IoT y servidores) conectados a la infraestructura física y virtualmente, localmente y en remoto, para que solo los activos autorizados reciban acceso y los activos no autorizados y sin gestionar sean identificados y se evite su acceso.',
    'Identificar', ctrl1Salvaguardas),
  calcControl(c2Id, 2, 'Inventario y Control de Activos de Software',
    'Gestionar activamente (inventariar, rastrear y corregir) todo el software (SO y aplicaciones) en la red para que solo el software autorizado esté instalado y pueda ejecutarse, y para que el software no autorizado y sin gestionar sea identificado y se evite su instalación o ejecución.',
    'Identificar', ctrl2Salvaguardas),
  calcControl(c3Id, 3, 'Protección de Datos',
    'Desarrollar procesos y controles técnicos para identificar, clasificar, gestionar de forma segura, retener y eliminar los datos.',
    'Proteger', ctrl3Salvaguardas),
  calcControl(c4Id, 4, 'Configuración Segura de Activos Empresariales y Software',
    'Establecer y mantener la configuración segura de los activos empresariales (dispositivos de usuario final, incluidos los dispositivos portátiles y remotos, dispositivos de red, dispositivos no informáticos/IoT y servidores) y el software (sistemas operativos y aplicaciones).',
    'Proteger', ctrl4Salvaguardas),
  calcControl(c5Id, 5, 'Gestión de Cuentas',
    'Usar procesos y herramientas para asignar y gestionar la autorización de las credenciales de las cuentas de usuario, de administrador y de servicio para los activos empresariales y el software.',
    'Proteger', ctrl5Salvaguardas),
  calcControl(c6Id, 6, 'Gestión del Control de Acceso',
    'Usar procesos y herramientas para crear, asignar, gestionar y revocar las credenciales de acceso y los privilegios para las cuentas de usuario, los administradores y los servicios en los activos empresariales y el software.',
    'Proteger', ctrl6Salvaguardas),
  calcControl(c7Id, 7, 'Gestión Continua de Vulnerabilidades',
    'Desarrollar un plan para evaluar e identificar continuamente las vulnerabilidades en los activos empresariales para remediarlas y minimizar la ventana de oportunidad para los atacantes.',
    'Identificar', ctrl7Salvaguardas),
  calcControl(c8Id, 8, 'Gestión de Registros de Auditoría',
    'Recopilar, alertar, revisar y retener los registros de auditoría de los eventos que puedan ayudar a detectar, comprender o recuperar de un ataque.',
    'Detectar', ctrl8Salvaguardas),
  calcControl(c9Id, 9, 'Protecciones de Correo Electrónico y Navegador Web',
    'Mejorar las protecciones y las detecciones de las amenazas a través del correo electrónico y los navegadores web, ya que son vectores de ataque principales.',
    'Proteger', ctrl9Salvaguardas),
  calcControl(c10Id, 10, 'Defensas contra Malware',
    'Prevenir o controlar la instalación, la difusión y la ejecución de aplicaciones, códigos o scripts maliciosos en los activos empresariales.',
    'Proteger', ctrl10Salvaguardas),
  calcControl(c11Id, 11, 'Recuperación de Datos',
    'Establecer y mantener las prácticas para la recuperación de datos suficientes para restaurar los activos empresariales dentro del alcance al estado previo al incidente.',
    'Recuperar', ctrl11Salvaguardas),
  calcControl(c12Id, 12, 'Gestión de Infraestructura de Red',
    'Establecer, implementar y gestionar activamente (rastrear, reportar y corregir) los dispositivos de red, para evitar que los atacantes exploten los servicios y los puntos de acceso de red vulnerables.',
    'Proteger', ctrl12Salvaguardas),
  calcControl(c13Id, 13, 'Monitoreo y Defensa de la Red',
    'Operar procesos y herramientas para establecer y mantener una monitorización comprensiva de la seguridad de la red y las defensas contra las amenazas a los activos y las amenazas a los datos de la empresa.',
    'Detectar', ctrl13Salvaguardas),
  calcControl(c14Id, 14, 'Concientización y Entrenamiento en Seguridad',
    'Establecer y mantener un programa de concientización sobre la seguridad para influir en el comportamiento de la fuerza de trabajo para estar al tanto de los riesgos de seguridad de la empresa.',
    'Proteger', ctrl14Salvaguardas),
  calcControl(c15Id, 15, 'Gestión de Proveedores de Servicios',
    'Desarrollar un proceso para evaluar a los proveedores de servicios que tengan acceso a los datos confidenciales de la empresa, o que sean responsables de los activos empresariales de la plataforma de TI para asegurarse de que protegen adecuadamente estas plataformas y datos.',
    'Identificar', ctrl15Salvaguardas),
  calcControl(c16Id, 16, 'Seguridad del Software de la Aplicación',
    'Gestionar el ciclo de vida de seguridad del software desarrollado o adquirido internamente para prevenir, detectar y remediar las debilidades de seguridad antes de que puedan afectar a la empresa.',
    'Proteger', ctrl16Salvaguardas),
  calcControl(c17Id, 17, 'Gestión de Respuesta a Incidentes',
    'Establecer un programa para desarrollar y mantener las capacidades de respuesta a incidentes (por ejemplo: políticas, planes, procedimientos, roles definidos, formación y comunicaciones) para preparar, detectar y responder rápidamente a un ataque.',
    'Responder', ctrl17Salvaguardas),
  calcControl(c18Id, 18, 'Pruebas de Penetración',
    'Poner a prueba la solidez de las defensas de la empresa (la tecnología, los procesos y las personas) simulando los objetivos y las acciones de un atacante.',
    'Identificar', ctrl18Salvaguardas),
];

// =====================================================
// AUDIT TRAIL MOCK
// =====================================================

export const mockCISAuditTrail: CISAuditEntry[] = [
  {
    id: 'cis-audit-001',
    entityType: 'salvaguarda',
    entityId: ctrl1Salvaguardas[0].id,
    action: 'status_changed',
    oldValue: 'not_started',
    newValue: 'in_progress',
    performedBy: 'Admin Sistema',
    performedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    notes: 'Iniciado inventario de activos empresariales',
  },
  {
    id: 'cis-audit-002',
    entityType: 'salvaguarda',
    entityId: ctrl3Salvaguardas[2].id,
    action: 'status_changed',
    oldValue: 'in_progress',
    newValue: 'implemented',
    performedBy: 'Admin Sistema',
    performedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    notes: 'ACLs configuradas en todos los sistemas críticos',
  },
  {
    id: 'cis-audit-003',
    entityType: 'salvaguarda',
    entityId: ctrl10Salvaguardas[0].id,
    action: 'status_changed',
    oldValue: 'in_progress',
    newValue: 'implemented',
    performedBy: 'Referente TI',
    performedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    notes: 'Anti-malware desplegado en todos los endpoints',
  },
];

// =====================================================
// FUNCIÓN DE CÁLCULO DE MÉTRICAS
// =====================================================

export function calculateCISMetrics(controls: CISControl[]): CISMetrics {
  const allSalvaguardas = controls.flatMap(c => c.salvaguardas);

  const implementedSalvaguardas = allSalvaguardas.filter(s => s.status === 'implemented').length;
  const inProgressSalvaguardas = allSalvaguardas.filter(s => s.status === 'in_progress').length;
  const notStartedSalvaguardas = allSalvaguardas.filter(s => s.status === 'not_started').length;
  const notApplicableSalvaguardas = allSalvaguardas.filter(s => s.status === 'not_applicable').length;

  const applicable = allSalvaguardas.filter(s => s.status !== 'not_applicable');
  const overallCompliance = applicable.length > 0
    ? Math.round(applicable.reduce((acc, s) => acc + s.complianceScore, 0) / applicable.length)
    : 0;

  // IG compliance
  const calcIGCompliance = (igKey: 'ig1' | 'ig2' | 'ig3') => {
    const igSalvaguardas = allSalvaguardas.filter(s => s[igKey] && s.status !== 'not_applicable');
    if (igSalvaguardas.length === 0) return 0;
    return Math.round(igSalvaguardas.reduce((acc, s) => acc + s.complianceScore, 0) / igSalvaguardas.length);
  };

  // By security function
  const secFunctions: CISSecurityFunction[] = ['Identificar', 'Proteger', 'Detectar', 'Responder', 'Recuperar'];
  const bySecurityFunction = secFunctions.reduce((acc, fn) => {
    const fnSalvaguardas = allSalvaguardas.filter(s => s.securityFunction === fn && s.status !== 'not_applicable');
    acc[fn] = {
      total: fnSalvaguardas.length,
      implemented: fnSalvaguardas.filter(s => s.status === 'implemented').length,
      percentage: fnSalvaguardas.length > 0
        ? Math.round(fnSalvaguardas.reduce((a, s) => a + s.complianceScore, 0) / fnSalvaguardas.length)
        : 0
    };
    return acc;
  }, {} as CISMetrics['bySecurityFunction']);

  // By asset type
  const assetTypes: CISAssetType[] = ['Dispositivos', 'Aplicaciones', 'Red', 'Datos', 'Usuarios', 'N/A'];
  const byAssetType = assetTypes.reduce((acc, at) => {
    const atSalvaguardas = allSalvaguardas.filter(s => s.assetType === at);
    acc[at] = {
      total: atSalvaguardas.length,
      implemented: atSalvaguardas.filter(s => s.status === 'implemented').length,
    };
    return acc;
  }, {} as CISMetrics['byAssetType']);

  // Critical gaps: IG1 salvaguardas not yet implemented
  const criticalGaps = allSalvaguardas.filter(s => s.ig1 && s.status === 'not_started');

  return {
    totalControls: controls.length,
    totalSalvaguardas: allSalvaguardas.length,
    implementedSalvaguardas,
    inProgressSalvaguardas,
    notStartedSalvaguardas,
    notApplicableSalvaguardas,
    overallCompliance,
    ig1Compliance: calcIGCompliance('ig1'),
    ig2Compliance: calcIGCompliance('ig2'),
    ig3Compliance: calcIGCompliance('ig3'),
    bySecurityFunction,
    byAssetType,
    criticalGaps,
  };
}

// =====================================================
// EXPORT HELPERS
// =====================================================

export function getCISControlsWithMetrics(): CISControl[] {
  return mockCISControls;
}

export function getAllCISSalvaguardas(): CISSalvaguarda[] {
  return mockCISControls.flatMap(c => c.salvaguardas);
}
